import surfaceGLSL from "./utils/surface.glsl";

import normalsGLSL from "./utils/normals.glsl";

const fragmentShader = /* glsl */ `
precision highp float;

varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vWorldPosition;

varying vec3 vObjectPosition;
varying vec3 vObjectNormal;
varying vec3 vHatchDirection;


/*
 * Shared shader utilities.
 */
${surfaceGLSL}
${normalsGLSL}


/*
 * Lighting.
 */
uniform vec3 uLightDirection;
uniform vec3 uLightColor;

uniform float uLightIntensity;
uniform float uAmbientIntensity;


/*
 * TAM.
 */
uniform vec3 uInkColor;
uniform float uHatchScale;

uniform sampler2D uTone0;
uniform sampler2D uTone1;
uniform sampler2D uTone2;
uniform sampler2D uTone3;
uniform sampler2D uTone4;
uniform sampler2D uTone5;


/*
 * ============================================================
 * TAM SAMPLING
 * ============================================================
 */

float sampleTone(
    int index,
    vec2 uv
) {

    if (index == 0) {
        return texture2D(
            uTone0,
            uv
        ).r;
    }

    if (index == 1) {
        return texture2D(
            uTone1,
            uv
        ).r;
    }

    if (index == 2) {
        return texture2D(
            uTone2,
            uv
        ).r;
    }

    if (index == 3) {
        return texture2D(
            uTone3,
            uv
        ).r;
    }

    if (index == 4) {
        return texture2D(
            uTone4,
            uv
        ).r;
    }

    return texture2D(
        uTone5,
        uv
    ).r;
}


float getHatchValue(
    float darkness,
    vec2 hatchUv
) {

    float tonePosition =
        darkness *
        6.0;


    if (
        tonePosition <
        1.0
    ) {

        float firstTone =
            sampleTone(
                0,
                hatchUv
            );


        return mix(
            1.0,
            firstTone,
            tonePosition
        );
    }


    float texturePosition =
        tonePosition -
        1.0;


    int lower =
        int(
            floor(
                texturePosition
            )
        );


    lower =
        min(
            lower,
            4
        );


    int upper =
        lower +
        1;


    float blend =
        fract(
            texturePosition
        );


    if (
        tonePosition >=
        5.999
    ) {

        return sampleTone(
            5,
            hatchUv
        );
    }


    float lowerTone =
        sampleTone(
            lower,
            hatchUv
        );


    float upperTone =
        sampleTone(
            upper,
            hatchUv
        );


    return mix(
        lowerTone,
        upperTone,
        blend
    );
}


/*
 * ============================================================
 * DIRECTION-ALIGNED PROJECTION
 * ============================================================
 */

/*
 * Rotate 2D projection coordinates so that
 * the hatch texture's horizontal direction
 * follows the projected curvature direction.
 */
vec2 orientProjection(
    vec2 uv,
    vec2 direction
) {

    float lengthSquared =
        dot(
            direction,
            direction
        );


    if (
        lengthSquared <
        0.000001
    ) {
        return uv;
    }


    direction =
        normalize(
            direction
        );


    /*
     * Perpendicular direction.
     */
    vec2 perpendicular =
        vec2(
            -direction.y,
            direction.x
        );


    /*
     * Express the existing projection in the
     * local direction/perpendicular frame.
     */
    return vec2(
        dot(
            uv,
            direction
        ),

        dot(
            uv,
            perpendicular
        )
    );
}


/*
 * ============================================================
 * CURVATURE-ALIGNED TRIPLANAR TAM
 * ============================================================
 */

float getTriplanarHatch(
    float darkness
) {

    vec3 objectNormal =
        normalize(
            vObjectNormal
        );


    vec3 hatchDirection =
        normalize(
            vHatchDirection
        );


    /*
     * Existing object-space projection
     * coordinates.
     */
    vec2 hatchX =
        vObjectPosition.yz;

    vec2 hatchY =
        vObjectPosition.xz;

    vec2 hatchZ =
        vObjectPosition.xy;


    /*
     * Project the 3D hatch direction into
     * each corresponding 2D projection plane.
     *
     * X projection -> YZ plane
     * Y projection -> XZ plane
     * Z projection -> XY plane
     */
    vec2 directionX =
        hatchDirection.yz;

    vec2 directionY =
        hatchDirection.xz;

    vec2 directionZ =
        hatchDirection.xy;


    hatchX =
        orientProjection(
            hatchX,
            directionX
        ) *
        uHatchScale;


    hatchY =
        orientProjection(
            hatchY,
            directionY
        ) *
        uHatchScale;


    hatchZ =
        orientProjection(
            hatchZ,
            directionZ
        ) *
        uHatchScale;


    /*
     * Standard triplanar blending.
     */
    vec3 blend =
        abs(
            objectNormal
        );


    blend =
        pow(
            blend,
            vec3(4.0)
        );


    blend /=
        max(
            blend.x +
            blend.y +
            blend.z,
            0.0001
        );


    float valueX =
        getHatchValue(
            darkness,
            hatchX
        );


    float valueY =
        getHatchValue(
            darkness,
            hatchY
        );


    float valueZ =
        getHatchValue(
            darkness,
            hatchZ
        );


    return (
        valueX * blend.x +
        valueY * blend.y +
        valueZ * blend.z
    );
}


/*
 * ============================================================
 * MAIN
 * ============================================================
 */

void main() {

    vec2 surfaceUv =
        transformSurfaceUv(
            vUv
        );


    vec3 surfaceColor =
        getSurfaceColor(
            surfaceUv
        );


    float ao =
        getAmbientOcclusion(
            surfaceUv
        );


    vec3 emissive =
        getEmissiveColor(
            surfaceUv
        );


    vec3 surfaceNormal =
        getSurfaceNormal(
            surfaceUv
        );


    vec3 lightDirection =
        normalize(
            uLightDirection
        );


    float diffuse =
        max(
            dot(
                surfaceNormal,
                lightDirection
            ),
            0.0
        );


    float ambient =
        uAmbientIntensity *
        ao;


    float illumination =
        clamp(
            ambient +
            diffuse *
            uLightIntensity,
            0.0,
            1.0
        );


    float darkness =
        1.0 -
        illumination;


    /*
     * Object-space triplanar TAM whose local
     * orientation follows the curvature-derived
     * surface direction.
     */
    float hatchValue =
        getTriplanarHatch(
            darkness
        );


    vec3 lighting =
        vec3(
            ambient
        ) +
        (
            uLightColor *
            diffuse *
            uLightIntensity
        );


    lighting =
        clamp(
            lighting,
            0.0,
            1.0
        );


    vec3 litSurface =
        surfaceColor *
        lighting;


    vec3 hatchedColor =
        mix(
            uInkColor,
            litSurface,
            hatchValue
        );


    vec3 finalColor =
        hatchedColor +
        emissive;


    gl_FragColor =
        vec4(
            finalColor,
            1.0
        );
}

`;

export default fragmentShader;
